import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, EMPTY, Observable, expand, forkJoin, map, reduce, switchMap, tap } from 'rxjs';
import { GroupService } from './group.service';
import { SocketService } from './socket.service';
import { PaginationInfo, PaginatedResponse } from './patient-appointment.service';

@Injectable({
  providedIn: 'root'
})
export class ScheduleService {
  private apiUrl = 'api';
  private eventsApiUrl = 'api/schedule-event';
  private scheduleSubject = new BehaviorSubject<any>(null);
  schedule$ = this.scheduleSubject.asObservable();
  get currentSchedule(): any { return this.scheduleSubject.value; }

  private editingEventSubject = new BehaviorSubject<any>(null);
  editingEvent$ = this.editingEventSubject.asObservable();

  private upcomingEventSubject = new BehaviorSubject<any[] | null>(null);
  private monthEventSubject = new BehaviorSubject<any[] | null>(null);
  private selectedMonthSubject = new BehaviorSubject<{ year: number, month: number } | null>(null);

  // Pagination subjects
  private upcomingPaginationSubject = new BehaviorSubject<PaginationInfo | null>(null);
  private isLoadingMoreSubject = new BehaviorSubject<boolean>(false);

  upcomingEvents$ = this.upcomingEventSubject.asObservable();
  monthEvents$ = this.monthEventSubject.asObservable();
  upcomingPagination$ = this.upcomingPaginationSubject.asObservable();
  isLoadingMore$ = this.isLoadingMoreSubject.asObservable();

  groupId?: string;
  private currentPage = 1;
  private pageSize = 50;

  constructor(
    private http: HttpClient,
    private groupService: GroupService,
    private socketService: SocketService,
  ) {
    this.groupService.selectedGroup$.pipe(
      switchMap(selectedGroup => {
        if (selectedGroup) {
          this.groupId = selectedGroup.id;
          this.currentPage = 1; // Reset page when group changes
          this.upcomingEventSubject.next([]);
          this.monthEventSubject.next([]);
          return forkJoin([
            this.getScheduleByGroup(selectedGroup.id).pipe(
              tap(schedules => this.scheduleSubject.next(schedules))
            ),
            this.loadGroupEvents()
          ]);
        }
        return EMPTY;
      }),
    ).subscribe();

    this.setupSocketListeners();
  }

  private setupSocketListeners(): void {
    // One saved event can change many occurrences (a recurring series), so a change refetches the
    // month on screen and the first page of upcoming events rather than patching rows in place.
    for (const name of ['eventAdded', 'eventUpdated', 'eventDeleted']) {
      this.socketService.listen(name, 'schedule-service').subscribe(() => this.refreshEvents());
    }

    this.socketService.listen('patientUpdated', 'schedule-service').subscribe((updatedPatient) => {
      // get the current values from the subjects in this service that could contain the patient
      const currentUpcoming = this.upcomingEventSubject.getValue();
      const currentMonth = this.monthEventSubject.getValue();

      // if the patient is in the upcoming list, update it
      if (currentUpcoming) {
        const updatedUpcoming = currentUpcoming.map(item => {
          if (item.Patient?.id === updatedPatient?.id) {
            return {
              ...item,
              Patient: {
                ...item.Patient,
                name: updatedPatient.name,
                surname: updatedPatient.surname,
                diagnosis: updatedPatient.diagnosis,
                patientNumber: updatedPatient.patientNumber,
                requiresValidation: updatedPatient.requiresValidation,
                requiresICDValidation: updatedPatient.requiresICDValidation,
                wasSelfBoarded: updatedPatient.wasSelfBoarded,
                status: updatedPatient.status,
              }
            };
          }
          return item;
        });
        this.upcomingEventSubject.next(updatedUpcoming);
      }

      // if the patient is in the month list, update it
      if (currentMonth) {
        const updatedMonth = currentMonth.map(item => {
          if (item.Patient.id === updatedPatient.id) {
            return {
              ...item,
              Patient: {
                ...item.Patient,
                name: updatedPatient.name,
                surname: updatedPatient.surname,
                diagnosis: updatedPatient.diagnosis,
                patientNumber: updatedPatient.patientNumber,
                requiresValidation: updatedPatient.requiresValidation,
                requiresICDValidation: updatedPatient.requiresICDValidation,
                wasSelfBoarded: updatedPatient.wasSelfBoarded,
                status: updatedPatient.status,
              }
            };
          }
          return item;
        });
        this.monthEventSubject.next(updatedMonth);
      }
    });
  }

  /** Reloads the month on screen and the first page of upcoming events after an event changed. */
  private refreshEvents(): void {
    const groupId = this.groupId;
    if (!groupId) return;
    const selected = this.selectedMonthSubject.getValue();
    const now = new Date();
    forkJoin({
      month: this.loadEventsByMonth(selected?.year ?? now.getFullYear(), selected?.month ?? now.getMonth() + 1),
      upcoming: this.loadEventsForTodayAndUpcoming(groupId, 1, this.pageSize),
    }).subscribe({
      next: ({ upcoming }) => {
        this.upcomingEventSubject.next(upcoming.data || []);
        this.upcomingPaginationSubject.next(upcoming.pagination || null);
      },
      error: () => { /* the next change or page load tries again */ },
    });
  }

  loadGroupEvents(): Observable<any> {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth() + 1;
    const groupId = this.groupId;

    if (!groupId) {
      return EMPTY;
    }
    // Load only current month + first page of upcoming (no unbounded load)
    return forkJoin({
      monthEvents: this.loadEventsByMonth(year, month),
      upcomingPage: this.loadEventsForTodayAndUpcoming(groupId, 1, this.pageSize)
    }).pipe(
      tap(({ monthEvents, upcomingPage }) => {
        this.monthEventSubject.next(monthEvents.data || []);
        this.upcomingEventSubject.next(upcomingPage.data || []);
        this.upcomingPaginationSubject.next(upcomingPage.pagination || null);
      })
    );
    /* const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth() + 1;
    const groupId = this.groupId;

    if (!groupId) {
      console.error('groupId is not set.');
      return EMPTY;
    }

    return forkJoin({
      monthAppointments: this.loadEventsByMonth(year, month),
      upcomingAppointments: this.loadEventsForTodayAndUpcoming(groupId, 1, this.pageSize)
    }).pipe(
      tap(results => {
        this.monthEventSubject.next(results.monthAppointments.data || []);
        this.upcomingEventSubject.next(results.upcomingAppointments.data || []);
        this.upcomingPaginationSubject.next(results.upcomingAppointments.pagination || null);
      })
    ); */
  }

  hasMoreUpcomingEvents(): boolean {
    const p = this.upcomingPaginationSubject.getValue();
    return !!(p?.hasMore);
  }

  /**
   * Load next page of upcoming events and append (for list "Load more").
   */
  loadMoreUpcomingEvents(): Observable<PaginatedResponse<any>> {
    const groupId = this.groupId;
    const pagination = this.upcomingPaginationSubject.getValue();
    if (!groupId || !pagination?.hasMore) {
      return EMPTY;
    }
    this.isLoadingMoreSubject.next(true);
    const nextPage = (pagination.currentPage || 1) + 1;
    return this.loadEventsForTodayAndUpcoming(groupId, nextPage, this.pageSize).pipe(
      tap((resp) => {
        const current = this.upcomingEventSubject.getValue() || [];
        this.upcomingEventSubject.next([...current, ...(resp.data || [])]);
        this.upcomingPaginationSubject.next(resp.pagination || null);
        this.isLoadingMoreSubject.next(false);
      })
    );
  }

  /**
   * @param options.skipSharedMonthCache When true, only fetches data and does not write
   * `monthEvents$` / selected month (used by the calendar for the second month a week runs into).
   */
  loadEventsByMonth(
    year: number,
    month: number,
    options?: { skipSharedMonthCache?: boolean },
  ): Observable<any> {
    const token = localStorage.getItem('token');
    const params = new HttpParams()
      .set('year', year.toString())
      .set('month', month.toString())
      .set('groupId', this.groupId!);

    return this.http.get<any[]>(`${this.eventsApiUrl}/calendar/month`, {
      params,
      headers: { Authorization: `Bearer ${token}` }
    }).pipe(
      tap((results: any) => {
        if (options?.skipSharedMonthCache) {
          return;
        }
        const appointments = results ? results.data : [];
        this.monthEventSubject.next(appointments);
        this.setSelectedMonth(year, month);
      })
    );
  }

  loadEventsForTodayAndUpcoming(groupId: string, page: number = 1, limit: number = 50): Observable<PaginatedResponse<any>> {
    const token = localStorage.getItem('token');
    const params = new HttpParams()
      .set('groupId', groupId)
      .set('page', page.toString())
      .set('limit', limit.toString());

    return this.http.get<any>(`${this.eventsApiUrl}/calendar/upcoming`, {
      params,
      headers: { Authorization: `Bearer ${token}` }
    });
  }
  private loadAllUpcomingEvents(): Observable<any[]> {
    return this.loadEventsForTodayAndUpcoming(this.groupId!, 1, this.pageSize).pipe(
      expand(resp =>
        resp.pagination.hasMore
          ? this.loadEventsForTodayAndUpcoming(
            this.groupId!,
            resp.pagination.currentPage + 1,
            this.pageSize
          )
          : EMPTY
      ),
      map(resp => resp.data),
      // explicit any[] to avoid TS inferring `never[]`
      reduce<any[], any[]>((all, pageData) => all.concat(pageData), [])
    );
  }

  getScheduleByGroup(groupId: string): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.get(`${this.apiUrl}/schedule/group/${groupId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  updateSchedule(schedule: any): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.put(`${this.apiUrl}/schedule/${schedule.id}`, schedule, {
      headers: { Authorization: `Bearer ${token}` }
    }).pipe(
      switchMap(() => this.getScheduleByGroup(this.groupId!))
    );
  }

  getEventById(id: string): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.get<any>(`${this.eventsApiUrl}/${id}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  addEvent(eventData: any): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.post<any>(this.eventsApiUrl, eventData, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  /**
   * On a recurring event `eventData.scope` ('occurrence' | 'following' | 'all') and
   * `eventData.occurrenceDate` say which occurrences the change applies to.
   */
  updateEvent(id: string, eventData: any): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.put<any>(`${this.eventsApiUrl}/${id}`, eventData, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  /** Deletes an event, or on a recurring event the occurrences `scope` names from `occurrenceDate`. */
  deleteEvent(id: string, scope?: 'occurrence' | 'following' | 'all', occurrenceDate?: string): Observable<any> {
    const token = localStorage.getItem('token');
    let params = new HttpParams();
    if (scope) params = params.set('scope', scope);
    if (occurrenceDate) params = params.set('occurrenceDate', occurrenceDate);
    return this.http.delete(`${this.eventsApiUrl}/${id}`, {
      params,
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  setEditingAppointment(item: any | null) {
    this.editingEventSubject.next(item);
  }

  setSelectedMonth(year: number, month: number) {
    this.selectedMonthSubject.next({ year, month });
  }
}